const DEFAULT_PAGE_SIZE = 8;

interface PagerProps {
  total: number;
  shown: number;
  pageSize?: number;
  showLoadMore?: boolean;
  onChange: (count: number) => void;
}

export default function Pager({ total, shown, pageSize = DEFAULT_PAGE_SIZE, showLoadMore = true, onChange }: PagerProps) {
  const currentPage = Math.max(1, Math.ceil(shown / pageSize));
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const hasMore = shown < total;

  const pages = Array.from({ length: totalPages }, (_, index) => index + 1).filter((page) => {
    if (totalPages <= 6) return true;
    return page <= 2 || page >= totalPages - 1 || Math.abs(page - currentPage) <= 1;
  });

  return (
    <div className="mt-6 flex flex-col items-center gap-4">
      {showLoadMore && hasMore && (
        <button
          type="button"
          onClick={() => onChange(Math.min(total, shown + pageSize))}
          className="flex items-center gap-1.5 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-5 py-2 text-sm text-foreground-800 transition hover:border-primary-400 hover:text-primary-700"
        >
          加载更多
          <i className="ri-arrow-down-line w-4 h-4 flex items-center justify-center" />
        </button>
      )}
      {!hasMore && total > pageSize && <p className="text-[11px] text-foreground-400">已经到底了，共 {total} 部作品</p>}

      {totalPages > 1 && (
        <div className="no-scrollbar flex max-w-full items-center gap-1.5 overflow-x-auto">
          <button
            type="button"
            disabled={currentPage === 1}
            onClick={() => onChange(Math.max(1, currentPage - 1) * pageSize)}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-background-300 text-foreground-600 transition hover:border-primary-400 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="上一页"
          >
            <i className="ri-arrow-left-s-line" />
          </button>
          {pages.map((page, index) => {
            const prev = pages[index - 1];
            return (
              <span key={page} className="flex shrink-0 items-center gap-1.5">
                {prev && page - prev > 1 && <span className="px-1 text-foreground-300">…</span>}
                <button
                  type="button"
                  onClick={() => onChange(page * pageSize)}
                  className={`flex h-8 min-w-8 items-center justify-center rounded-md px-2 text-xs transition ${
                    page === currentPage
                      ? 'bg-primary-500 font-medium text-background-50'
                      : 'border border-background-300 text-foreground-700 hover:border-primary-400'
                  }`}
                >
                  {page}
                </button>
              </span>
            );
          })}
          <button
            type="button"
            disabled={currentPage === totalPages}
            onClick={() => onChange(Math.min(totalPages, currentPage + 1) * pageSize)}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-background-300 text-foreground-600 transition hover:border-primary-400 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="下一页"
          >
            <i className="ri-arrow-right-s-line" />
          </button>
        </div>
      )}
    </div>
  );
}